"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";

const spring = { type: "spring" as const, stiffness: 300, damping: 40 };

const TEXT_CARD = "Sneaky";
const IMAGE_CARD_ID = "39";
const IMAGE_DESCRIPTION = "What if card image";

const getImageUrl = (id: string) => {
  const paddedId = id.padStart(2, "0");
  return `/cards/rotated/WHATIF_B_American-poker-size-${paddedId}.jpg`;
};

export default function DemoDriftCards() {
  const [wordFlipped, setWordFlipped] = useState(false);
  const [imageFlipped, setImageFlipped] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          setTimeout(() => setWordFlipped(true), 600);
          setTimeout(() => setImageFlipped(true), 1400);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full scrolly-reveal">
      <div className="stack gap-6">

        {/* Text Card */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3">
            Word Card
          </p>
          <div
            className="relative aspect-8/5 w-full"
            style={{ perspective: "640px" }}
          >
            <motion.div
              className="w-full h-full relative"
              style={{ transformStyle: "preserve-3d" }}
              animate={{ rotateX: wordFlipped ? -180 : 0 }}
              transition={spring}
            >
              {/* Front — revealed word */}
              <div
                className="absolute inset-0 bg-white text-black flex items-center justify-center overflow-hidden"
                style={{
                  backfaceVisibility: "hidden",
                  borderRadius: "5%",
                  boxShadow: "0 -5px 10px 0 rgba(0,0,0,0.5)",
                  transform: "rotateX(-180deg)",
                  padding: "clamp(1.5rem, 2vh, 3rem) clamp(1.5rem, 2vw, 3rem)",
                }}
              >
                <p
                  className="font-black text-black leading-[0.85] text-center"
                  style={{ fontSize: "clamp(1.75rem, 15cqw, 3rem)" }}
                >
                  {TEXT_CARD}
                </p>
              </div>
              {/* Back — face down */}
              <div
                className="absolute inset-0 bg-black text-white flex items-center justify-center"
                style={{
                  backfaceVisibility: "hidden",
                  borderRadius: "5%",
                  boxShadow: "0 -5px 10px 0 rgba(0,0,0,0.5)",
                }}
              >
                <h2
                  className="font-bold text-white leading-[0.85] text-left"
                  style={{ fontSize: "clamp(2rem, 8vw, 4rem)" }}
                >
                  <span style={{ letterSpacing: "-0.05em" }}>W</span>hat
                  <br />
                  If?
                </h2>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Image Card */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3">
            Image Card
          </p>
          <div
            className="relative aspect-8/5 w-full"
            style={{ perspective: "640px" }}
          >
            <motion.div
              className="w-full h-full relative"
              style={{ transformStyle: "preserve-3d" }}
              animate={{ rotateX: imageFlipped ? -180 : 0 }}
              transition={spring}
            >
              {/* Front — image */}
              <div
                className="absolute inset-0 bg-white overflow-hidden"
                style={{
                  backfaceVisibility: "hidden",
                  borderRadius: "5%",
                  boxShadow: "0 -5px 10px 0 rgba(0,0,0,0.5)",
                  transform: "rotateX(-180deg)",
                }}
              >
                <Image
                  src={getImageUrl(IMAGE_CARD_ID)}
                  alt={IMAGE_DESCRIPTION}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, 50vw"
                  style={{ transform: "scale(1.35)" }}
                />
              </div>
              {/* Back — face down */}
              <div
                className="absolute inset-0 bg-white text-black flex items-center justify-center"
                style={{
                  backfaceVisibility: "hidden",
                  borderRadius: "5%",
                  boxShadow: "0 -5px 10px 0 rgba(0,0,0,0.5)",
                }}
              >
                <h2
                  className="font-bold text-black leading-[0.85] text-left"
                  style={{ fontSize: "clamp(2rem, 8vw, 4rem)" }}
                >
                  <span style={{ letterSpacing: "-0.05em" }}>W</span>hat
                  <br />
                  If?
                </h2>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <p className="scrolly-reveal-fade text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold text-center">
        These have nothing to do with your challenge.{" "}
        <span className="text-gray-700">That&rsquo;s the point.</span>
      </p>
    </div>
  );
}
